import React, { useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatTurkeyLocalCompact } from '../../../utils/turkeyLocalDateTime';

const MeetingQRCodeDialog = ({ open, onClose, meeting }) => {
  const loadJsPdf = useCallback(async () => {
    if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
    await new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      el.async = true;
      el.onload = resolve;
      el.onerror = reject;
      document.body.appendChild(el);
    });
    return window.jspdf.jsPDF;
  }, []);

  const toDataUrl = async (url) => {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const sanitizeFileName = (name) => {
    return String(name || 'Meeting').replace(/[^\p{L}\p{N}_\- ]/gu, '').replace(/\s+/g, '_');
  };

  const toAscii = (str) => {
    if (!str) return '';
    const map = {
      'ç': 'c', 'Ç': 'C', 'ğ': 'g', 'Ğ': 'G', 'ı': 'i', 'İ': 'I', 'ö': 'o', 'Ö': 'O', 'ş': 's', 'Ş': 'S', 'ü': 'u', 'Ü': 'U',
    };
    return String(str).replace(/[çÇğĞıİöÖşŞüÜ]/g, (ch) => map[ch] || ch).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  const formatDateTime = (date) => formatTurkeyLocalCompact(date);

  if (!meeting) return null;

  // Meeting için JSON formatında veri: { "id": "...", "type": "Meeting" }
  const qrData = JSON.stringify({ id: meeting.id || meeting.Id, type: 'Meeting' });
  const data = encodeURIComponent(qrData);
  const qrUrl512 = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${data}`;
  const qrUrl1024 = `https://api.qrserver.com/v1/create-qr-code/?size=1024x1024&data=${data}`;
  const baseName = `${sanitizeFileName(meeting.title)}_Qr_Code`;

  const handleDownloadPDF = async () => {
    try {
      const jsPDF = await loadJsPdf();
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 48;
      const qrSize = Math.min(400, pageWidth - margin * 2);
      const x = (pageWidth - qrSize) / 2;
      const dataUrl = await toDataUrl(qrUrl1024);
      doc.addImage(dataUrl, 'PNG', x, 96, qrSize, qrSize);
      const titleAscii = toAscii(String(meeting.title || ''));
      const meetingDateStr = meeting.meetingDate ? formatDateTime(meeting.meetingDate) : '';
      const baseY = 96 + qrSize + 28;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleAscii, pageWidth / 2, baseY, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      if (meetingDateStr) {
        doc.text(`Toplanti Tarihi: ${meetingDateStr}`, pageWidth / 2, baseY + 22, { align: 'center' });
      }
      if (meeting.location) {
        const locationAscii = toAscii(String(meeting.location));
        doc.text(`Lokasyon: ${locationAscii}`, pageWidth / 2, baseY + 38, { align: 'center' });
      }
      doc.save(`${baseName}.pdf`);
    } catch (e) { }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Toplantı QR Kodu
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">Toplantı: {meeting.title}</Typography>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <img src={qrUrl512} alt="QR" style={{ width: 256, height: 256 }} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          component="a"
          href={qrUrl1024}
          download={`${sanitizeFileName(meeting.title)}_Qr_Kod.png`}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          PNG olarak indir
        </Button>
        <Button
          onClick={handleDownloadPDF}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          PDF olarak indir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetingQRCodeDialog;
